/**
 * KPI Detail Store - 실적상세 패널 상태 및 CRUD (리팩토링 버전)
 *
 * @description 월별 실적 관리 (조회/등록/수정/삭제)
 *
 * @usage
 * const { fetchMonthlyRecords, saveActual, deleteActual } = useKpiDetailStore();
 */
import { useKpiApi } from '~/composables/performance/kpi/useKpiApi';
import { useOkrState } from '~/composables/performance/kpi/useOkrStore';
import { fetchPlanActivitiesForTab } from '~/composables/domain/activity/fetchPlanActivitiesForTab';

// 공유 상태 import
import {
  selectedYear,
  selectedMember,
  loginUserInfo,
  kpiList,
  selectedKpi,
  monthlyRecords,
  targetHistoryList,
  detailLoading,
  feedbackMonList, // KPI 피드백 등록 가능 월 목록
  memberTree,
  selectedDept,
  memberScoreVersion,
  kpiActivityPlanList,
  kpiActivityLoading,
} from './useKpiSharedState';

// 공통 유틸리티 import
import { mapKpiListItem } from './useKpiUtils';
import { mapKpiActualListToMonthlyRecords } from './mapKpiActualListToMonthlyRecords';

import type { MonthlyRecord, ActualListResponseItem, SaveActualData } from './useKpiTypes';

// ============================================
// Store Export
// ============================================
export const useKpiDetailStore = () => {
  const kpiApi = useKpiApi();
  const { selectedCycleId } = useOkrState();

  /**
   * 피드백 등록 가능 월 조회
   */
  const fetchFeedbackMonList = async () => {
    if (!selectedKpi.value) return []; // KPI가 선택되지 않은 경우 빈 배열 반환

    try {
      const response = await kpiApi.fetchKpiFeedbackMonList(
        selectedYear.value, // 연도
        selectedKpi.value.kpiId, // KPI ID
      );

      feedbackMonList.value = response?.feedbackMonList || []; // KPI 피드백 등록 가능 월 목록
      return feedbackMonList.value; // KPI 피드백 등록 가능 월 목록
    } catch (error) {
      console.error('피드백 월 목록 조회 실패:', error);
      feedbackMonList.value = [];
      return [];
    }
  };

  /**
   * 월별 실적 조회
   */
  const fetchMonthlyRecords = async () => {
    if (!selectedKpi.value) return;

    try {
      detailLoading.value.monthly = true;

      // 참조 형태(shareGbn='shared')인 경우만 원본 KPI(upKpiId)의 실적을 조회
      const isReferenceType = selectedKpi.value.shareGbn === 'shared' && selectedKpi.value.upKpiId;

      const kpiIdToFetch = isReferenceType
        ? selectedKpi.value.upKpiId || ''
        : selectedKpi.value.kpiId;

      const response = await kpiApi.fetchActualList(
        selectedYear.value,
        selectedMember.value || '',
        kpiIdToFetch,
      );

      // 목표변경이력 저장
      targetHistoryList.value = response?.targetHistoryList || [];

      if (response?.actualList && Array.isArray(response.actualList)) {
        monthlyRecords.value = mapKpiActualListToMonthlyRecords(response.actualList, {
          kpiTypeCd: selectedKpi.value?.kpiTypeCd,
          accrueMethodCd: selectedKpi.value?.accrueMethodCd,
          rateMethodCd: selectedKpi.value?.rateMethodCd,
        });

        // 월별 실적 조회 후 kpiList의 달성률도 동기화
        if (monthlyRecords.value.length > 0) {
          const latestRecord = monthlyRecords.value[0];
          const isQualitative = selectedKpi.value?.kpiTypeCd === '02';
          // 비계량: 최신 월 진척률, 계량: 누적달성률
          const syncedAchieve = isQualitative
            ? latestRecord.progress
            : latestRecord.accumAchieveRate;
          const syncedActual = isQualitative ? latestRecord.progress : latestRecord.accumActualVal;

          // selectedKpi 업데이트
          selectedKpi.value = {
            ...selectedKpi.value,
            accrueAchieve: syncedAchieve,
            actualStr: String(syncedActual),
          };

          // kpiList에서 해당 KPI도 업데이트
          const kpiIndex = kpiList.value.findIndex(k => k.kpiId === selectedKpi.value?.kpiId);
          if (kpiIndex !== -1) {
            kpiList.value[kpiIndex] = {
              ...kpiList.value[kpiIndex],
              accrueAchieve: syncedAchieve,
              actualStr: String(syncedActual),
            };
          }
        }
      } else {
        monthlyRecords.value = [];
      }
    } catch (error) {
      console.error('월별실적 조회 실패:', error);
      monthlyRecords.value = [];
    } finally {
      detailLoading.value.monthly = false;
    }
  };

  /**
   * 월별 실적 추가 (로컬)
   */
  const addMonthlyRecord = (record: MonthlyRecord) => {
    monthlyRecords.value.push(record);
  };

  /**
   * 월별 실적 수정 (로컬)
   */
  const updateMonthlyRecord = (record: MonthlyRecord) => {
    const index = monthlyRecords.value.findIndex(r => r.actualId === record.actualId);
    if (index !== -1) {
      monthlyRecords.value[index] = record;
    }
  };

  /**
   * KPI 목록 새로고침 및 선택 복원
   * @description 실적 저장/삭제 후 KPI 목록을 재조회하고 선택 상태를 복원
   */
  const refreshKpiListAndSelection = async () => {
    const currentKpiId = selectedKpi.value?.kpiId;
    const kpiResponse = await kpiApi.fetchKpiList(
      selectedYear.value,
      selectedMember.value || undefined,
    );

    if (kpiResponse?.kpiList && Array.isArray(kpiResponse.kpiList)) {
      kpiList.value = kpiResponse.kpiList.map(mapKpiListItem);

      if (currentKpiId) {
        const updatedKpi = kpiList.value.find(k => k.kpiId === currentKpiId);
        if (updatedKpi) {
          selectedKpi.value = updatedKpi;
        }
      }
    }

    // ★ MemberPanel 도넛 점수 갱신을 위해 memberTree 재조회
    try {
      const treeResponse = await kpiApi.fetchMemberTreeList(
        selectedYear.value,
        selectedDept.value || undefined,
        selectedMember.value || undefined,
      );
      if (treeResponse?.memberTreeList) {
        memberTree.value = treeResponse.memberTreeList;
      }
    } catch (e) {
      // memberTree 갱신 실패 시 무시 (KPI 목록은 이미 갱신됨)
    }
  };

  /**
   * 실적 저장 (등록/수정)
   */
  const saveActual = async (data: SaveActualData) => {
    try {
      const userId = selectedMember.value || loginUserInfo.value?.userId || '';

      const response = await kpiApi.saveActual({
        year: selectedYear.value,
        kpiId: selectedKpi.value?.kpiId,
        userId,
        ...data,
      });

      const hasError = response?.msg && response?.result !== 'SUCCESS';
      if (!hasError && response) {
        await fetchMonthlyRecords();
        await refreshKpiListAndSelection();
        memberScoreVersion.value++;
      }

      return response;
    } catch (error) {
      console.error('실적 저장 실패:', error);
      throw error;
    }
  };

  /**
   * 실적 삭제
   */
  const deleteActual = async (mon: string | number) => {
    if (!selectedKpi.value) return;

    try {
      const response = await kpiApi.deleteActual(
        selectedYear.value,
        selectedKpi.value.kpiId,
        String(mon).padStart(2, '0'),
      );

      const hasError = response?.msg && response?.result !== 'SUCCESS';
      if (!hasError && response) {
        await fetchMonthlyRecords();
        await refreshKpiListAndSelection();
        memberScoreVersion.value++;
      }

      return response;
    } catch (error) {
      console.error('실적 삭제 실패:', error);
      throw error;
    }
  };

  /**
   * 활동 탭 - KPI에 연결된 업무(Plan) 목록 조회
   * - 업무 직접 연동(objRefGbnCd='002'): 이미 로드된 planList(체크된 항목)를 그대로 사용
   * - OKR 연동(objRefGbnCd='001'): 연결된 모든 KR 각각에 대해 selectKrPlanMapping.do(fetchOkrTaskMapping)를
   *   호출해 나온 업무 목록을 모두 합쳐서 표시 (KPI → KR → 업무 계층 구조를 한 번에 펼쳐서 보여줌)
   *
   * @param linkType - DetailPanel propertyForm.linkType ('plan' | 'okr')
   * @param okrList - DetailPanel에 이미 로드된 연동 OKR(KR) 목록 (item.planId = krId)
   * @param planList - DetailPanel에 이미 로드된 연동 업무 목록
   */
  const loadKpiActivityPlanList = async (
    linkType: string,
    okrList: Array<Record<string, any>> = [],
    planList: Array<Record<string, any>> = [],
  ) => {
    if (!selectedKpi.value) {
      kpiActivityPlanList.value = [];
      return;
    }

    kpiActivityLoading.value = true;
    try {
      const normalize = (p: Record<string, any>) => ({
        ...p,
        activityList: Array.isArray(p.activityList) ? p.activityList : [],
        activityCnt: Number(p.activityCnt) || 0,
      });

      if (linkType === 'plan') {
        kpiActivityPlanList.value = (planList || [])
          .filter(item => item.checked === 'checked')
          .map(normalize);
        return;
      }

      // OKR 연동: 연결된 모든 KR의 업무를 모아서 표시
      // ※ fetchOkrList는 분기(cycleId)와 무관하게 해당 유저/KPI의 전체 분기 KR을 내려주고
      //   checked 플래그만 세팅한다 (백엔드가 findCycleId를 사실상 무시함).
      //   따라서 트리(useOkrStore.fetchGoalKpiOkrList)와 동일하게 현재 선택된 분기(boardId)로
      //   한 번 더 걸러야 다른 분기의 과거 연동 KR 업무까지 섞여 나오는 것을 막을 수 있다.
      const userId = selectedMember.value || loginUserInfo.value?.userId || '';
      const linkedKrIds = (okrList || [])
        .filter(item => {
          if (item.checked !== 'checked') return false;
          if (!selectedCycleId.value) return true;
          return (item.boardId || item.cycleId) === selectedCycleId.value;
        })
        .map(item => item.planId)
        .filter(Boolean);

      if (linkedKrIds.length === 0) {
        kpiActivityPlanList.value = [];
        return;
      }

      const results = await Promise.all(
        linkedKrIds.map(krId => kpiApi.fetchOkrTaskMapping(selectedYear.value, userId, krId).catch(() => null)),
      );

      kpiActivityPlanList.value = results.flatMap(res => res?.krPlanList || []).map(normalize);
    } catch (error) {
      console.error('KPI 연결 업무 활동 조회 실패:', error);
      kpiActivityPlanList.value = [];
    } finally {
      kpiActivityLoading.value = false;
    }
  };

  /**
   * 활동 탭 - 연결 업무(Plan) 1건 펼침 시 활동 목록 지연 조회
   */
  const handleShowKpiPlanActivity = async (planId: string) => {
    const planIndex = kpiActivityPlanList.value.findIndex(p => String(p.planId) === String(planId));
    if (planIndex < 0) return;

    const plan = kpiActivityPlanList.value[planIndex];
    const activityList = await fetchPlanActivitiesForTab(plan, selectedYear.value);
    kpiActivityPlanList.value[planIndex] = { ...plan, activityList };
  };

  /**
   * 등록 가능한 월 목록 조회
   */
  const fetchAvailableMonths = async () => {
    try {
      const allMonths = Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1).padStart(2, '0'),
        label: `${i + 1}월`,
      }));

      // 이미 등록된 월 제외
      const registeredMonths = monthlyRecords.value.map(r => String(r.actualMon).padStart(2, '0'));
      return allMonths.filter(m => !registeredMonths.includes(m.value));
    } catch (error) {
      console.error('등록 가능 월 조회 실패:', error);
      return [];
    }
  };

  return {
    // KPI 피드백 관련
    feedbackMonList,
    fetchFeedbackMonList,

    // 상태
    monthlyRecords,
    targetHistoryList,
    detailLoading,
    selectedKpi,

    // 조회
    fetchMonthlyRecords,
    fetchAvailableMonths,

    // CRUD
    addMonthlyRecord,
    updateMonthlyRecord,
    saveActual,
    deleteActual,

    // 활동 탭 (연결 업무)
    kpiActivityPlanList,
    kpiActivityLoading,
    loadKpiActivityPlanList,
    handleShowKpiPlanActivity,
  };
};
